import { Button, message } from 'antd';
import { isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import HeaderWithColumnSelectionExport from '~/components/organisms/Shared/HeaderWithColumnSelectionExport';
import { SaleStatementTypeEnum } from '~/domain/enum/salesStatement/saleStatementType.enum';
import { ISalesStatementExportColumn } from '~/domain/model/SalesStatement/SalesStatement';
import { buildExportCheckedKeys } from '~/helpers/util/buildExportCheckedKeys';
import { postSalesStatementExport } from '~/services/producer/SalesStatement/SalesStatementService';
import useSaleStatementByTypeStore from '~/zustand/salesStatement/useSalesStatementByTypeStore';
import { IExportColumn } from '../../Shared/GenericExportModal/GenericExportModal';

export const title = 'Extrato de Vendas de Intermediação';
export const subtitle =
  'Acompanhe os números de vendas de Intermediação. Analise, filtre e extraia relatórios.';

const EXPORT_COLUMNS: Omit<IExportColumn, 'checked'>[] = [
  { key: 'salesChannel', label: 'Origem' },
  { key: 'transactionDate', label: 'Data do Pedido' },
  { key: 'billingDate', label: 'Data de Entrega' },
  { key: 'commercialDate', label: 'Data Comercial' },
  { key: 'orderNumber', label: 'Nº Pedido' },
  { key: 'siteCode', label: 'Chave' },
  { key: 'customerDescription', label: 'Cliente' },
  { key: 'productLongDescription', label: 'Produto' },
  { key: 'ProductCode', label: 'Código do Produto' },
  { key: 'qualityDescription', label: 'QLD.' },
  { key: 'packingCode', label: 'Embalagem' },
  { key: 'packingDescription', label: 'Descrição da Embalagem' },
  { key: 'quantity', label: 'QE x QpE' },
  { key: 'totalQuantity', label: 'Quant. Total' },
  { key: 'unitValue', label: 'Valor Unit.' },
  { key: 'packingValue', label: 'Valor Emb.' },
  { key: 'freightValue', label: 'Valor Frete' },
  { key: 'totalValue', label: 'Valor Total' },
];

const EXPORT_COLUMN_KEYS = EXPORT_COLUMNS.map(column => column.key);

/**
 * Colunas da exportação sem correspondente na tabela (ProductCode, packingDescription)
 * ficam sempre marcadas — o usuário não tem como ocultá-las em Filtrar Colunas.
 */
const TABLE_KEY_BY_EXPORT_KEY: Record<string, string> = {
  salesChannel: 'salesChannel',
  transactionDate: 'transactionDate',
  billingDate: 'billingDate',
  commercialDate: 'commercialDate',
  orderNumber: 'orderNumber',
  siteCode: 'siteCode',
  customerDescription: 'customerDescription',
  productLongDescription: 'productDescription',
  qualityDescription: 'qualityDescription',
  packingCode: 'packingCode',
  quantity: 'quantity',
  totalQuantity: 'totalQuantity',
  unitValue: 'unitValue',
  packingValue: 'packingValue',
  freightValue: 'freightValue',
  totalValue: 'totalValue',
};

export default function SalesStatementIntermediationHeader() {
  const {
    formInstance,
    isDisabledButton,
    isDisabledButtonSummary,
    getExportParams,
    visibleColumnKeys,
  } = useSaleStatementByTypeStore(
    useShallow(state => {
      return {
        formInstance: state.intermediationFormFilterInstance,
        isDisabledButton: isEmpty(state.intermediationTable.data),
        isDisabledButtonSummary: isEmpty(state.intermediationTable.data),
        getExportParams: state.getExportParams,
        visibleColumnKeys: state.intermediationVisibleColumnKeys,
      };
    }),
  );

  const handleOpenModal = useCallback(async () => {
    try {
      await formInstance?.validateFields?.();
    } catch (errorInfo) {
      message.warning(
        'Erro ao exportar os Extratos de Vendas de Intermediação. Por favor, verifique o formulário e tente novamente mais tarde',
      );
      throw errorInfo;
    }
  }, [formInstance]);

  const handleGetExcelExport = async (selectedColumns: string[]) => {
    await formInstance?.validateFields?.();
    const fields = await formInstance?.getFieldsValue?.();
    if (!fields)
      return Promise.reject(
        new Error(
          'Erro ao exportar os Extratos de Vendas de Intermediação. Por favor, verifique o formulário e tente novamente mais tarde',
        ),
      );

    const columns = selectedColumns.reduce(
      (acc, column) => ({ ...acc, [column]: true }),
      {} as ISalesStatementExportColumn,
    );

    const params = getExportParams(SaleStatementTypeEnum.Intermediation, columns);
    return postSalesStatementExport(params);
  };

  const exportColumns: IExportColumn[] = useMemo(() => {
    const checkedKeys = buildExportCheckedKeys(
      EXPORT_COLUMN_KEYS,
      TABLE_KEY_BY_EXPORT_KEY,
      visibleColumnKeys,
    );

    return EXPORT_COLUMNS.map(column => ({ ...column, checked: checkedKeys.has(column.key) }));
  }, [visibleColumnKeys]);

  const handleSummaryTable = () => {
    const summaryElement = document.getElementById('summary-intermediation');
    if (summaryElement) {
      summaryElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const additionalAction = (
    <Button
      type="ghost"
      size="large"
      onClick={handleSummaryTable}
      disabled={isDisabledButtonSummary}
    >
      Resumo Intermediação
    </Button>
  );

  return (
    <HeaderWithColumnSelectionExport
      title={title}
      subtitle={subtitle}
      isDisabledButton={isDisabledButton}
      fileName="relatório-extrato-vendas-intermediação.xlsx"
      onClickOpenModal={handleOpenModal}
      getExcelExport={handleGetExcelExport}
      exportColumns={exportColumns}
      additionalAction={additionalAction}
    />
  );
}
